/**
 * IntakeProcessor -- Agent Workroom for the 3-agent intake pipeline.
 *
 * Ported from JSX (master:frontend/src/components/IntakeProcessor.jsx)
 * to TypeScript with Shadcn UI patterns.
 *
 * Pipeline execution lives in pipelineManager.ts (module-level, outside React)
 * so it survives session switching. This component subscribes for live updates.
 *
 * On mount: starts pipeline if not running, or subscribes to existing one.
 * On unmount: unsubscribes; pipeline keeps running in background.
 */

import { FC, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Loader2, ChevronDown } from 'lucide-react'
import { cn } from '../lib/utils'
import {
  startPipeline,
  getSnapshot,
  subscribe,
  clearPipeline,
  hasPipeline,
  PIPELINE_AGENT_IDS,
} from '../services/pipelineManager'
import type {
  PipelineSnapshot,
  AgentPipelineStatus,
  CaseyPreviewData,
} from '../services/pipelineManager'
import { parsePanelResponse } from '../utils/clinicalDataParser'

// ────────────────────────────────────────────────────────
// Types & Constants
// ────────────────────────────────────────────────────────

interface IntakeProcessorProps {
  workspaceId: string
  onComplete: (results: Record<string, unknown>) => void
  className?: string
  /** True while intake file uploads are still in flight — suppresses the
   *  mount auto-start so the pipeline can't run against a partial chart. */
  uploadsPending?: boolean
}

interface AgentTheme {
  bg: string
  text: string
  border: string
  dot: string
  dotPulse: string
  label: string
}

const AGENT_THEME: Record<string, AgentTheme> = {
  'hpi-pe-agent': {
    bg: 'bg-violet-500/15',
    text: 'text-violet-300',
    border: 'border-violet-500/30',
    dot: 'bg-violet-400',
    dotPulse: 'bg-violet-400/60',
    label: 'The Historian',
  },
  'casey': {
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-300',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-400',
    dotPulse: 'bg-emerald-400/60',
    label: 'Casey',
  },
  'clinical-reasoning': {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/30',
    dot: 'bg-amber-400',
    dotPulse: 'bg-amber-400/60',
    label: 'A&P Agent',
  },
}

/** IDs for the 3 status cards shown in the UI (casey slots consolidated into one) */
const DISPLAY_CARD_IDS = ['hpi-pe-agent', 'casey', 'clinical-reasoning'] as const
const CASEY_SLOT_IDS = ['casey-extract', 'casey-data', 'casey-clinical'] as const

/** Labels for the activity feed — casey sub-slots get specific names */
const EVENT_LABELS: Record<string, string> = {
  'hpi-pe-agent': 'The Historian',
  'casey-extract': 'Casey (Extractions)',
  'casey-data': 'Casey (Data)',
  'casey-clinical': 'Casey (Clinical)',
  'clinical-reasoning': 'A&P Agent',
}

/** Derive a single combined status from the 3 casey sub-slots */
function getCombinedCaseyStatus(agentStatus: Record<string, AgentPipelineStatus>): AgentPipelineStatus {
  const statuses = CASEY_SLOT_IDS.map(id => agentStatus[id])
  if (statuses.some(s => s === 'error')) return 'error'
  if (statuses.every(s => s === 'complete')) return 'complete'
  if (statuses.every(s => s === 'complete' || s === 'data-ready')) return 'data-ready'
  if (statuses.some(s => s === 'running' || s === 'data-ready')) return 'running'
  return 'waiting'
}

const formatTime = (s: number): string =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

// ────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────

export const IntakeProcessor: FC<IntakeProcessorProps> = ({
  workspaceId,
  onComplete,
  className,
  uploadsPending = false,
}) => {
  const [snapshot, setSnapshot] = useState<PipelineSnapshot | null>(() => getSnapshot(workspaceId))
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  // Start pipeline or subscribe to existing one. While uploads are pending,
  // do neither: subscribe() no-ops when no pipeline exists, so this effect
  // must re-run (uploadsPending dep) once WorkspaceManager starts the
  // pipeline after the last upload lands.
  useEffect(() => {
    if (uploadsPending) return
    if (!hasPipeline(workspaceId)) {
      startPipeline(workspaceId, workspaceId)
    }
    setSnapshot(getSnapshot(workspaceId))
    return subscribe(workspaceId, setSnapshot)
  }, [workspaceId, uploadsPending])

  // Auto-enter workspace if pipeline was already complete on mount
  useEffect(() => {
    const snap = getSnapshot(workspaceId)
    if (!snap) return
    const caseyIds = ['casey-extract', 'casey-data', 'casey-clinical'] as const
    const coreDone = caseyIds.every(id => snap.agentStatus[id] === 'complete' || snap.agentStatus[id] === 'error')
    if (snap.status === 'complete' || snap.status === 'error' || coreDone) {
      const results = snap.results || {}
      const allDone = PIPELINE_AGENT_IDS.every(id =>
        snap.agentStatus[id] === 'complete' || snap.agentStatus[id] === 'error'
      )
      if (allDone) clearPipeline(workspaceId)
      onCompleteRef.current(results)
    }
  }, [workspaceId])

  // Derived state
  const agentStatus = snapshot?.agentStatus || Object.fromEntries(PIPELINE_AGENT_IDS.map(id => [id, 'waiting' as AgentPipelineStatus]))
  const isComplete = snapshot?.status === 'complete'
  const isSettled = isComplete || snapshot?.status === 'error'
  const coreReady = (['casey-extract', 'casey-data'] as const).every(id => agentStatus[id] === 'complete' || agentStatus[id] === 'error')
  const hasError = snapshot?.error || null
  const caseyPreview = snapshot?.caseyPreview || null
  const realEvents = snapshot?.events || []


  // Merged events sorted by timestamp — exclude cosmetic thinking events
  const events = useMemo(
    () => realEvents.sort((a, b) => a.timestamp - b.timestamp),
    [realEvents]
  )

  // Elapsed time tracking
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (isSettled) return
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [isSettled])

  const elapsed = snapshot?.startTime ? Math.max(0, Math.floor((now - snapshot.startTime) / 1000)) : 0
  const agentElapsed = useMemo(() => {
    const result: Record<string, number> = {}
    ;PIPELINE_AGENT_IDS.forEach(id => {
      const start = snapshot?.agentStartTimes?.[id]
      if (!start) { result[id] = 0; return }
      const end = snapshot?.agentEndTimes?.[id] || (snapshot?.agentStatus?.[id] === 'running' ? now : start)
      result[id] = Math.max(0, Math.floor((end - start) / 1000))
    })
    return result
  }, [snapshot, now])

  // Auto-scroll activity feed
  const feedRef = useRef<HTMLDivElement>(null)
  const feedShouldAutoScroll = useRef(true)
  const handleFeedScroll = useCallback(() => {
    const el = feedRef.current
    if (!el) return
    feedShouldAutoScroll.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100
  }, [])
  useEffect(() => {
    if (feedRef.current && feedShouldAutoScroll.current) {
      requestAnimationFrame(() => {
        if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight
      })
    }
  }, [events.length])

  // Enter workspace
  const handleEnterWorkspace = useCallback(() => {
    const results = snapshot?.results || {}
    const allDone = PIPELINE_AGENT_IDS.every(id =>
      agentStatus[id] === 'complete' || agentStatus[id] === 'error'
    )
    if (allDone) clearPipeline(workspaceId)
    onComplete(results)
  }, [snapshot, workspaceId, onComplete, agentStatus])

  // Progress bar
  const displayStatuses = DISPLAY_CARD_IDS.map(id =>
    id === 'casey' ? getCombinedCaseyStatus(agentStatus) : agentStatus[id]
  )
  const completedCount = displayStatuses.filter(s => s === 'complete').length
  const runningCount = displayStatuses.filter(s => s === 'running' || s === 'data-ready').length
  const progressPct = Math.min(95, Math.round((completedCount / 3) * 100 + (runningCount > 0 ? 5 : 0)))

  return (
    <div className={cn('flex-1 flex items-center justify-center p-6', className)}>
      <div className={cn('w-full transition-all duration-500', caseyPreview ? 'max-w-5xl' : 'max-w-2xl')}>
        {/* Header */}
        <div className="text-center mb-5">
          <h2 className="text-xl font-bold text-foreground mb-1">Agent Workroom</h2>
          <p className="text-xs text-muted-foreground">
            {isComplete
              ? `All agents finished in ${formatTime(elapsed)} -- your workspace is ready`
              : hasError
                ? 'An error occurred during processing'
                : 'Processing...'}
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-5">
          <div className="h-1 rounded-full bg-muted overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-700 ease-out',
                isComplete ? 'bg-green-500' : hasError ? 'bg-amber-500' : 'bg-primary'
              )}
              style={{ width: isSettled ? '100%' : `${progressPct}%` }}
            />
          </div>
        </div>

        <div className={caseyPreview ? 'grid grid-cols-5 gap-4' : ''}>
          <div className={caseyPreview ? 'col-span-3' : ''}>
            {/* Agent status cards */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              {DISPLAY_CARD_IDS.map(id => {
                const theme = AGENT_THEME[id]
                const status = id === 'casey'
                  ? getCombinedCaseyStatus(agentStatus)
                  : agentStatus[id] as AgentPipelineStatus
                const cardTime = id === 'casey'
                  ? Math.max(...CASEY_SLOT_IDS.map(s => agentElapsed[s] || 0))
                  : agentElapsed[id]
                const isActive = status === 'running' || status === 'data-ready'
                return (
                  <div
                    key={id}
                    className={cn(
                      'relative rounded-xl px-3 py-2.5 border text-center transition-all duration-300 overflow-hidden',
                      isActive ? `${theme.bg} ${theme.border}` : '',
                      status === 'complete' ? 'bg-green-500/10 border-green-500/20' : '',
                      status === 'error' ? 'bg-destructive/10 border-destructive/20' : '',
                      status === 'waiting' ? 'bg-muted border-border opacity-50' : ''
                    )}
                  >
                    <div className="relative z-10">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        {isActive ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : status === 'complete' ? (
                          <span className="text-green-400 text-xs">{'\u2713'}</span>
                        ) : status === 'error' ? (
                          <span className="text-destructive text-xs">{'\u2717'}</span>
                        ) : (
                          <span className="text-muted-foreground text-xs">{'\u25CB'}</span>
                        )}
                        <span className={cn(
                          'text-xs font-semibold',
                          isActive ? theme.text :
                          status === 'complete' ? 'text-green-400' :
                          status === 'error' ? 'text-destructive' :
                          'text-muted-foreground'
                        )}>
                          {theme.label}
                        </span>
                      </div>
                      {(isActive || status === 'complete') && (
                        <span className="text-[10px] text-muted-foreground tabular-nums">
                          {formatTime(cardTime)}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Activity feed */}
            <div
              ref={feedRef}
              onScroll={handleFeedScroll}
              className="rounded-xl border bg-card overflow-y-auto mb-4"
              style={{ maxHeight: '45vh', minHeight: '180px' }}
            >
              {events.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin mr-2" />
                  Starting agents...
                </div>
              ) : (
                <div className="p-3 space-y-1">
                  {events.map(evt => {
                    const theme = AGENT_THEME[evt.agentId] || AGENT_THEME['casey']
                    const isThinking = evt.type === 'thinking'
                    return (
                      <div
                        key={evt.id}
                        className={cn(
                          'flex items-start gap-2 text-xs',
                          isThinking ? 'opacity-70' : ''
                        )}
                      >
                        <span className={cn(
                          'mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full',
                          evt.type === 'error' ? 'bg-destructive' :
                          evt.type === 'complete' ? 'bg-green-400' :
                          isThinking ? `${theme.dotPulse} animate-pulse` :
                          theme.dot
                        )} />
                        <span className={cn(
                          'flex-shrink-0 font-medium w-24 truncate text-[11px]',
                          evt.type === 'error' ? 'text-destructive' :
                          isThinking ? 'text-muted-foreground' :
                          theme.text
                        )}>
                          {EVENT_LABELS[evt.agentId] || theme.label}
                        </span>
                        <span className={cn(
                          'flex-shrink-0 rounded px-1.5 py-0.5 text-[9px] leading-none font-mono uppercase tracking-wider',
                          evt.type === 'started' ? 'bg-muted text-muted-foreground' :
                          evt.type === 'tool_call' ? 'bg-amber-500/15 text-amber-300' :
                          evt.type === 'thinking' ? 'bg-muted text-muted-foreground/50' :
                          evt.type === 'complete' ? 'bg-green-500/15 text-green-300' :
                          evt.type === 'error' ? 'bg-destructive/15 text-destructive' :
                          'bg-muted text-muted-foreground'
                        )}>
                          {evt.type === 'tool_call' ? 'tool' : evt.type}
                        </span>
                        <span className={cn(
                          'break-words min-w-0',
                          isThinking ? 'text-muted-foreground italic' : 'text-foreground/80'
                        )}>
                          {evt.description}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Error message */}
            {hasError && (
              <div className="mb-4 px-4 py-2 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">
                {hasError}
              </div>
            )}

            {/* Enter Workspace button */}
            <button
              onClick={handleEnterWorkspace}
              disabled={!coreReady && !isSettled}
              className={cn(
                'w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200',
                coreReady || isSettled
                  ? hasError
                    ? 'bg-amber-600/80 hover:bg-amber-600 text-white shadow-lg'
                    : 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              )}
            >
              {coreReady && !hasError
                ? 'Enter Workspace \u2192'
                : isSettled || coreReady
                  ? `Enter Workspace ${hasError ? '(partial results)' : ''} \u2192`
                  : `Processing... ${formatTime(elapsed)}`}
            </button>
          </div>

          {/* Right column: Casey patient preview */}
          {caseyPreview && (
            <div className="col-span-2">
              <div className="rounded-xl border bg-card overflow-y-auto" style={{ maxHeight: '60vh' }}>
                <div className="sticky top-0 bg-card border-b px-4 py-2.5 z-10">
                  <h3 className="text-sm font-semibold text-foreground">Patient Preview</h3>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    AI-drafted Patient Summary
                  </p>
                </div>
                <div className="p-4">
                  <CollapsibleClinicalPreview data={caseyPreview} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────
// Collapsible Clinical Preview sub-component
// ────────────────────────────────────────────────────────

const CollapsibleSection: FC<{
  title: string
  count?: number
  defaultOpen?: boolean
  children: React.ReactNode
}> = ({ title, count, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border border-border/40 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-foreground bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <span>
          {title}
          {count != null && count > 0 && (
            <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">({count})</span>
          )}
        </span>
        <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="px-3 py-2">{children}</div>}
    </div>
  )
}

const CollapsibleClinicalPreview: FC<{ data: CaseyPreviewData }> = ({ data }) => {
  const parsed = useMemo(
    () => data.panelContent ? parsePanelResponse(data.panelContent) : null,
    [data.panelContent],
  )

  const oneLiner = parsed?.summary?.oneLiner || data.patientSummary
  const hasStructuredSummary = data.presentation || data.admissionContext
  const hasParsedSections = parsed && (parsed.summary || parsed.orders || parsed.medications)

  // No parsed sections AND no structured summary — show one-liner only
  if (!hasParsedSections && !hasStructuredSummary) {
    return (
      <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-2.5">
        <p className="text-xs font-semibold text-clinical-teal leading-snug">
          {oneLiner}
        </p>
      </div>
    )
  }

  // Structured summary from save_clinical_data (no panelContent)
  if (!hasParsedSections && hasStructuredSummary) {
    return (
      <div className="space-y-2">
        <CollapsibleSection title="Patient Summary" defaultOpen>
          <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-2.5 mb-2">
            <p className="text-xs font-semibold text-clinical-teal leading-snug">
              {oneLiner}
            </p>
          </div>
          {data.presentation && (
            <div className="mb-2">
              <h4 className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Presentation</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">{data.presentation}</p>
            </div>
          )}
          {data.admissionContext && (
            <div>
              <h4 className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Admission Context</h4>
              <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">{data.admissionContext}</p>
            </div>
          )}
        </CollapsibleSection>
      </div>
    )
  }

  // At this point hasParsedSections is true, so parsed is non-null
  const p = parsed!

  return (
    <div className="space-y-2">
      {/* Patient Summary — panelContent parsed path */}
      <CollapsibleSection title="Patient Summary" defaultOpen>
        <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-2.5 mb-2">
          <p className="text-xs font-semibold text-clinical-teal leading-snug">
            {oneLiner}
          </p>
        </div>
        {p.summary?.presentation && (
          <div className="mb-2">
            <h4 className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Presentation</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">{p.summary.presentation}</p>
          </div>
        )}
        {p.summary?.admissionContext && (
          <div>
            <h4 className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Admission Context</h4>
            <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">{p.summary.admissionContext}</p>
          </div>
        )}
      </CollapsibleSection>

      {/* Admit Orders */}
      {p.orders && p.orders.sections.length > 0 && (
        <CollapsibleSection title="Admit Orders" count={p.orders.sections.length}>
          <div className="space-y-2">
            {p.orders.sections.map((section, i) => (
              <div key={i}>
                <h4 className="text-[10px] font-semibold text-foreground/80 uppercase tracking-wider mb-1">
                  {section.title}
                </h4>
                <ul className="space-y-0.5">
                  {section.items.map((item, j) => (
                    <li key={j} className="text-xs text-muted-foreground leading-relaxed pl-2 border-l border-border/40">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Medication Reconciliation */}
      {p.medications && (
        <CollapsibleSection
          title="Medication Reconciliation"
          count={
            p.medications.continue.length +
            p.medications.hold.length +
            p.medications.adjust.length +
            p.medications.discontinue.length
          }
        >
          <div className="space-y-2">
            {p.medications.continue.length > 0 && (
              <MedCategory label="Continue" items={p.medications.continue} color="text-green-400" />
            )}
            {p.medications.hold.length > 0 && (
              <MedCategory label="Hold" items={p.medications.hold} color="text-amber-400" />
            )}
            {p.medications.adjust.length > 0 && (
              <MedCategory label="Adjust" items={p.medications.adjust} color="text-blue-400" />
            )}
            {p.medications.discontinue.length > 0 && (
              <MedCategory label="Discontinue" items={p.medications.discontinue} color="text-red-400" />
            )}
            {p.medications.drug_interactions.length > 0 && (
              <div>
                <h4 className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider mb-1">Drug Interactions</h4>
                <ul className="space-y-0.5">
                  {p.medications.drug_interactions.map((item, i) => (
                    <li key={i} className="text-xs text-muted-foreground leading-relaxed pl-2 border-l border-amber-500/30">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p.medications.prophylaxis_gaps.length > 0 && (
              <div>
                <h4 className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-1">Prophylaxis Gaps</h4>
                <ul className="space-y-0.5">
                  {p.medications.prophylaxis_gaps.map((item, i) => (
                    <li key={i} className="text-xs text-muted-foreground leading-relaxed pl-2 border-l border-blue-500/30">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  )
}

const MedCategory: FC<{ label: string; items: string[]; color: string }> = ({ label, items, color }) => (
  <div>
    <h4 className={cn('text-[10px] font-semibold uppercase tracking-wider mb-1', color)}>
      {label} <span className="text-muted-foreground font-normal">({items.length})</span>
    </h4>
    <ul className="space-y-0.5">
      {items.map((item, i) => (
        <li key={i} className="text-xs text-muted-foreground leading-relaxed pl-2 border-l border-border/40">
          {item}
        </li>
      ))}
    </ul>
  </div>
)


